/**
 * Production Records filter row — the timeline toolbar's contents.
 *
 * Left: the day-status PILL TABS, All / Incomplete / Complete. Single-valued
 * on purpose: the two states are mutually exclusive and exhaustive, so a
 * multi-select could only ever say "all" a second way. "Incomplete" carries
 * a red count of the incomplete campus-days in the loaded window; that count
 * replaces the alert that used to sit above the list (Abner, Sep 14 evening:
 * no banner, tabs on the left with a red dot, the selects on the right, no
 * status dropdown).
 *
 * Right (`ml-auto` in the panel's own flex toolbar):
 *   - Campus:     multi-select pill, MULTI-CAMPUS schools only. It took over
 *                 from the campus tabs when every campus became a card under
 *                 the day (PROD-1279 part two); empty = every campus.
 *   - DateWindow: single-select pill (this week / last week / past 30 /
 *                 past 60 days). The `/days` query window resolves from this
 *                 preset; it is the only server-side filter.
 *   - Mealtime:   multi-select pill over every mealtime the platform
 *                 schedules; keeps days that schedule one of them. Multi is
 *                 right here — Breakfast AND Lunch is a real request.
 *
 * The tabs are `@ordo/ui/tabs` in the pill recipe the serve header uses
 * (muted track, white active pill), never `Button` CTAs. A thin
 * presentational shell — state lives in the parent page. It returns a
 * fragment so the panel's toolbar row lays the two groups out itself.
 */

import { cn } from '@ordo/ui/cn'
import { FilterMultiSelect, FilterSelect } from '@ordo/ui/filter-select'
import { Tabs, TabsList, TabsTrigger } from '@ordo/ui/tabs'

import {
  DATE_WINDOW_OPTIONS,
  type DateWindow,
  DAY_STATUS_OPTIONS,
  type DayStatusChoice,
  type FilterState,
  type Mealtime,
  MEALTIME_OPTIONS,
} from '@/api/production-records'

export interface FilterRowProps {
  state: FilterState
  onChange: (next: FilterState) => void
  /** Incomplete campus-days in the loaded window (after the mealtime and campus filters): the red count on the Incomplete tab. */
  incompleteCount: number
  /** The campuses the user oversees; the campus select renders only for two or more. */
  campuses?: ReadonlyArray<{ id: string; name: string }>
}

/** The red count on the Incomplete tab. Nothing to do renders nothing, never a red zero. */
export function IncompleteCountDot({ count }: { count: number }) {
  if (count <= 0) {
    return null
  }
  return (
    <span
      data-testid="incomplete-count"
      aria-label={`${count} incomplete ${count === 1 ? 'day' : 'days'}`}
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold tabular-nums text-white dark:bg-red-500">
      {count}
    </span>
  )
}

export interface DayStatusTabsProps {
  value: DayStatusChoice
  onChange: (next: DayStatusChoice) => void
  incompleteCount: number
}

/** All / Incomplete / Complete as pill tabs; Incomplete wears the red count. */
export function DayStatusTabs({ value, onChange, incompleteCount }: DayStatusTabsProps) {
  return (
    <Tabs value={value} onValueChange={(next) => onChange(next as DayStatusChoice)} className="min-w-0">
      <TabsList
        aria-label="Day status"
        className="h-9 rounded-full border border-border bg-muted p-[3px] text-foreground">
        {DAY_STATUS_OPTIONS.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            data-testid={`status-tab-${option.value}`}
            className={cn(
              'h-full gap-2 rounded-full px-3 py-1 text-sm font-medium text-muted-foreground',
              'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
            )}>
            {option.label}
            {option.value === 'pending' ? <IncompleteCountDot count={incompleteCount} /> : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

export function FilterRow({ state, onChange, incompleteCount, campuses = [] }: FilterRowProps) {
  return (
    <>
      <DayStatusTabs
        value={state.status}
        onChange={(status) => onChange({ ...state, status })}
        incompleteCount={incompleteCount}
      />
      <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="filter-selects">
        {campuses.length > 1 ? (
          <FilterMultiSelect
            values={state.campuses as Set<string>}
            onChange={(next) => onChange({ ...state, campuses: next as ReadonlySet<string> })}
            options={campuses.map((campus) => ({ value: campus.id, label: campus.name }))}
            placeholder="All campuses"
            filterName="campuses"
          />
        ) : null}
        <FilterSelect
          value={state.dateWindow}
          onChange={(v) => onChange({ ...state, dateWindow: v as DateWindow })}
          // Copied at the boundary: the primitive's `options` is mutable, our
          // source of truth is not.
          options={DATE_WINDOW_OPTIONS.map((o) => ({ ...o }))}
        />
        <FilterMultiSelect
          values={state.mealtimes as Set<string>}
          onChange={(next) => onChange({ ...state, mealtimes: next as ReadonlySet<Mealtime> })}
          options={MEALTIME_OPTIONS.map((o) => ({ ...o }))}
          placeholder="All mealtimes"
          filterName="mealtimes"
        />
      </div>
    </>
  )
}
